import liff from '@line/liff';
import { readOrgLocation, type OrgConfig } from './orgContext';
import { fetchOrgConfig } from './api';

/**
 * ตัวห่อ LIFF SDK
 *
 * กติกาความปลอดภัยข้อเดียวที่ห้ามละเมิด:
 *   ห้ามส่ง liff.getProfile().userId ไปให้ backend เชื่อถือตรง ๆ — ค่านั้นปลอมได้
 *   ต้องใช้ getIDToken() แล้วให้ Worker verify กับ LINE เท่านั้น
 */

/**
 * โหมดพัฒนาต้อง **เปิดเองอย่างชัดเจน** เท่านั้น
 *
 * เดิมเขียนว่า "ไม่มี LIFF ID = โหมดพัฒนา" ซึ่งทำให้ build ที่ลืมใส่ LIFF ID
 * ขึ้น production แล้วรันเป็นโหมดพัฒนาเงียบ ๆ — isLoggedIn() คืน true
 * ทั้งที่ไม่มีใครล็อกอิน และ getIdToken() คืน null จนไปพังที่ปลายทาง
 * โดยไม่มีอะไรบอกว่าสาเหตุคือ config ที่ขาด
 */
export const isDevMode = import.meta.env.VITE_LIFF_DEV === '1';

/**
 * ค่าที่ฝังตอน build — ใช้ได้เฉพาะกรณีมูลนิธิเดียว
 *
 * เก็บไว้เป็นทางถอย ไม่ใช่ทางหลัก: ถ้า API ตอบมาแล้วค่าจาก API ชนะเสมอ
 * เพราะมันคือค่าที่ตรงกับมูลนิธิใน URL จริง ๆ
 */
const BUILD_TIME_LIFF_ID = import.meta.env.VITE_LIFF_ID as string | undefined;

export const org = readOrgLocation();

let config: OrgConfig | null = null;
let ready: Promise<OrgConfig> | null = null;

/** ข้อมูลมูลนิธิที่โหลดมาแล้ว — เรียกได้หลัง initLiff() สำเร็จเท่านั้น */
export function orgConfig(): OrgConfig | null {
  return config;
}

/**
 * โหลด config ของมูลนิธิ แล้วค่อย init LIFF ด้วย LIFF ID ที่ถูกตัว
 *
 * ลำดับนี้กลับกันไม่ได้ — liff.init() ต้องรู้ id ตั้งแต่วินาทีแรก
 * และ id ที่ถูกต้องขึ้นกับว่าเปิดมาจากมูลนิธิไหน
 */
export function initLiff(): Promise<OrgConfig> {
  ready ??= boot();
  return ready;
}

async function boot(): Promise<OrgConfig> {
  const remote = await fetchOrgConfig('staff').catch((err: unknown) => {
    // ต่อ API ไม่ได้เลยตั้งแต่ก้าวแรก — บอกให้ชัดว่าเป็นเรื่องเครือข่าย/ตั้งค่า
    // ไม่ใช่ปล่อยไปพังที่ liff.init() แล้วผู้ใช้เห็นข้อความของ LINE ที่ไม่เกี่ยวกัน
    throw new Error(
      `โหลดข้อมูลมูลนิธิ "${org.slug}" ไม่ได้ — ${(err as Error).message ?? 'ติดต่อเซิร์ฟเวอร์ไม่ได้'}`,
    );
  });

  config = remote;

  if (isDevMode) return remote;

  const liffId = remote.liffId || BUILD_TIME_LIFF_ID;
  if (!liffId) {
    throw new Error(
      `มูลนิธิ "${remote.nameTh}" ยังไม่ได้ตั้งค่า LIFF ของเจ้าหน้าที่ — ` +
        'ให้ผู้ดูแลกรอก LINE Login channel ที่หน้าตั้งค่าระบบก่อน',
    );
  }

  await liff.init({ liffId });
  return remote;
}

export function isLoggedIn(): boolean {
  return isDevMode ? true : liff.isLoggedIn();
}

export function login(): void {
  if (!isDevMode) liff.login({ redirectUri: window.location.href });
}

export function getIdToken(): string | null {
  if (isDevMode) return import.meta.env.VITE_DEV_ID_TOKEN ?? null;
  return liff.getIDToken();
}

export function getDisplayName(): string | null {
  if (isDevMode) return 'โหมดพัฒนา';
  try {
    return liff.getDecodedIDToken()?.name ?? null;
  } catch {
    return null;
  }
}

export function closeWindow(): void {
  if (!isDevMode && liff.isInClient()) liff.closeWindow();
  else window.history.back();
}

export function isInClient(): boolean {
  return isDevMode ? false : liff.isInClient();
}

/** เปิดแผนที่นำทางไปยังพิกัดบ้าน */
export function openNavigation(lat: number, lng: number): void {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  if (!isDevMode && liff.isInClient()) liff.openWindow({ url, external: true });
  else window.open(url, '_blank', 'noopener');
}

export function openTel(phone: string): void {
  window.location.href = `tel:${phone}`;
}
